import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import XmarketLogo from './icons/XmarketLogo';
import UserMenu from './UserMenu';
import MobileTabBar from './MobileTabBar';
import { isAuthenticated, getCachedUser } from '../utils/auth';
import { getUnreadMessageCount } from '../utils/messageCount';
import { ALL_GAME_NAMES, getGameSlug } from '../config/allGames';
import { getGameIcon } from '../utils/getGameIcon';
import { GAME_SEARCH_ALIASES } from '../config/gameSearchAliases';
import { getGameServiceCategories } from '../config/gameServiceCategories';
import { rankedSearchGames, matchesGame, sortGamesBySearchRelevance } from '../utils/gameCatalogSearch';
import { useLanguage } from '../contexts/LanguageContext';
import ProfileDsNav from './ProfileDsNav';
import '../styles/DsHome.css';

const NAV = [
  { category: 'accounts', label: 'Accounts', ico: '/ds/nav-cards.svg', match: (path) => path.includes('/accounts') },
  { category: 'top-ups', label: 'Top-ups', ico: '/ds/nav-badge.svg', match: (path) => path.includes('/top-ups') },
  { category: 'boosting', label: 'Boosting', ico: '/ds/nav-target.svg', match: (path) => path.includes('/boosting') },
  { category: 'items', label: 'Items', ico: '/ds/nav-play.svg', match: (path) => path.includes('/items') },
  { category: 'currency', label: 'Currency', ico: '/ds/nav-podium.svg', match: (path) => path.includes('/currency') },
];

const SIDE_LINKS = [
  { to: '/profile', label: 'Favorites', ico: '/ds/side-favorites.svg', auth: true },
  { to: '/fortnite/accounts', label: 'All Games', ico: '/ds/side-all.svg' },
  { href: '/#ds-popular-heading', label: 'Popular Games', ico: '/ds/side-popular.svg' },
];

const SIDE_CATEGORIES = [
  { to: '/fortnite/accounts', label: 'Accounts', ico: '/ds/nav-cards.svg' },
  { to: '/brawl-stars/top-ups', label: 'Top-ups', ico: '/ds/nav-badge.svg' },
  { to: '/fortnite/items', label: 'Items', ico: '/ds/nav-play.svg' },
  { to: '/valorant/boosting', label: 'Boosting', ico: '/ds/nav-target.svg' },
  { to: '/roblox/currency', label: 'Currency', ico: '/ds/nav-podium.svg' },
];

const SOCIALS = [
  { href: 'https://discord.gg/xmarket', label: 'Discord', ico: '/ds/social-discord.svg' },
  { href: 'https://x.com/xmarketgg', label: 'X', ico: '/ds/social-x.svg' },
  { href: 'https://www.instagram.com/xmarket.gg/', label: 'Instagram', ico: '/ds/social-instagram.svg' },
];

function listGamesForCategory(categoryKey, query) {
  if (!categoryKey) return [];
  let names = ALL_GAME_NAMES.filter((name) =>
    getGameServiceCategories(getGameSlug(name)).some((service) => service.key === categoryKey)
  );
  const trimmed = query.trim();
  if (trimmed) {
    names = sortGamesBySearchRelevance(
      names.filter((name) => matchesGame(name, trimmed, GAME_SEARCH_ALIASES)),
      trimmed,
      GAME_SEARCH_ALIASES
    );
  } else {
    names = [...names].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }
  return names.map((game) => ({
    game,
    href: `/${getGameSlug(game)}/${categoryKey}`,
    icon: getGameIcon(game),
  }));
}

function SideItem({ to, href, state, ico, label, active }) {
  const className = `ds-side-item${active ? ' is-on' : ''}`;
  const body = (
    <>
      {ico ? <span className="ds-side-item-ico" style={{ '--ico': `url(${ico})` }} aria-hidden /> : null}
      <span className="ds-side-item-label">{label}</span>
    </>
  );
  if (href) {
    return <a className={className} href={href}>{body}</a>;
  }
  return (
    <Link className={className} to={to} state={state}>
      {body}
    </Link>
  );
}

const LANG_FLAGS = {
  en: '/flags/gb.svg',
  es: '/flags/es.svg',
  fr: '/flags/fr.svg',
  de: '/flags/de.svg',
  it: '/flags/it.svg',
  pt: '/flags/br.svg',
  ru: '/flags/ru.svg',
  'zh-CN': '/flags/other.svg',
  ja: '/flags/jp.svg',
  ko: '/flags/kr.svg',
  ar: '/flags/ae.svg',
  hi: '/flags/in.svg',
};

export default function DsHomeShell({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const loggedIn = isAuthenticated();
  const isSeller = Boolean(loggedIn && getCachedUser()?.is_seller);
  const { currentLanguage, changeLanguage, currentLanguageData, supportedLanguages } = useLanguage();
  const [query, setQuery] = useState('');
  const [unread, setUnread] = useState(0);
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  const [langOpen, setLangOpen] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const [catQuery, setCatQuery] = useState('');
  const langRef = useRef(null);
  const catRef = useRef(null);
  const catTimer = useRef(null);
  const sideScrollRef = useRef(null);

  useEffect(() => {
    if (!loggedIn) {
      setUnread(0);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const count = await getUnreadMessageCount();
      if (!cancelled) setUnread(count);
    };
    load();
    const id = window.setInterval(load, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [loggedIn]);

  useEffect(() => {
    if (!langOpen) return undefined;
    const onPointer = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setLangOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [langOpen]);

  useEffect(() => {
    setCatQuery('');
  }, [openCat]);

  useEffect(() => {
    setOpenCat(null);
  }, [location.pathname]);

  useEffect(() => {
    if (sideScrollRef.current) {
      sideScrollRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!openCat) return undefined;
    const onPointer = (event) => {
      if (catRef.current && !catRef.current.contains(event.target)) {
        setOpenCat(null);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpenCat(null);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openCat]);

  const keepCat = () => {
    if (catTimer.current) {
      window.clearTimeout(catTimer.current);
      catTimer.current = null;
    }
  };

  const openCategory = (key) => {
    keepCat();
    setOpenCat(key);
    setLangOpen(false);
  };

  const closeCategorySoon = () => {
    keepCat();
    catTimer.current = window.setTimeout(() => setOpenCat(null), 160);
  };

  const catGames = useMemo(
    () => listGamesForCategory(openCat, catQuery),
    [openCat, catQuery]
  );
  const openCatLabel = NAV.find((item) => item.category === openCat)?.label || '';

  const results = useMemo(
    () => rankedSearchGames(ALL_GAME_NAMES, query, GAME_SEARCH_ALIASES, { limit: 8 }),
    [query]
  );

  const authTo = (path) => (loggedIn ? path : '/auth?signin=true');
  const authState = loggedIn ? undefined : { backgroundLocation: location };
  const flagSrc = LANG_FLAGS[currentLanguage] || '/flags/gb.svg';
  const isProfile = location.pathname.startsWith('/profile');
  const sellerMode =
    location.pathname.startsWith('/profile/seller') ||
    location.pathname.startsWith('/profile/become-seller');

  return (
    <div className={`ds-home${children ? '' : ' ds-home--chrome'}`}>
      <header className="ds-nav">
        <div className="ds-nav-leading">
          <Link to="/" className="ds-nav-logo" aria-label="XMARKET">
            <XmarketLogo />
          </Link>
          <nav className="ds-nav-links" aria-label="Main" ref={catRef}>
            {NAV.map((item) => {
              const active = item.match(location.pathname) || openCat === item.category;
              return (
                <div
                  key={item.label}
                  className="ds-nav-item"
                  onMouseEnter={() => openCategory(item.category)}
                  onMouseLeave={closeCategorySoon}
                >
                  <button
                    type="button"
                    className={`ds-nav-link${active ? ' is-active' : ''}`}
                    aria-expanded={openCat === item.category}
                    aria-haspopup="true"
                    onClick={() => (
                      openCat === item.category ? setOpenCat(null) : openCategory(item.category)
                    )}
                  >
                    <span className="ds-nav-ico" style={{ '--ico': `url(${item.ico})` }} aria-hidden />
                    <span>{item.label}</span>
                    <CaretDown
                      size={12}
                      className={`ds-nav-caret${openCat === item.category ? ' is-open' : ''}`}
                    />
                  </button>
                </div>
              );
            })}
            {openCat ? (
              <div
                className="ds-cat-menu"
                onMouseEnter={keepCat}
                onMouseLeave={closeCategorySoon}
              >
                <div className="ds-cat-toolbar">
                  <div className="ds-cat-heading">
                    <p className="ds-cat-kicker">Browse</p>
                    <h3>{openCatLabel}</h3>
                    <span className="ds-cat-count">{catGames.length}</span>
                  </div>
                  <label className="ds-cat-search">
                    <MagnifyingGlass size={16} />
                    <input
                      type="search"
                      placeholder="Search games"
                      value={catQuery}
                      onChange={(event) => setCatQuery(event.target.value)}
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </label>
                </div>
                <div className="ds-cat-list">
                  {catGames.length === 0 ? (
                    <p className="ds-cat-empty">No games match that search.</p>
                  ) : (
                    <div className="ds-cat-grid">
                      {catGames.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="ds-cat-game"
                          onClick={() => setOpenCat(null)}
                        >
                          {item.icon ? (
                            <img
                              src={item.icon}
                              alt=""
                              width={32}
                              height={32}
                              loading="lazy"
                              decoding="async"
                              onError={(event) => {
                                const src = event.target?.src;
                                if (src && src.endsWith('.avif')) {
                                  event.target.src = src.replace(/\.avif$/i, '.webp');
                                }
                              }}
                            />
                          ) : (
                            <span className="ds-cat-fallback">{item.game.charAt(0)}</span>
                          )}
                          <span>{item.game}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </nav>
          <div className="ds-nav-search">
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpenCat(null);
              }}
              placeholder="Search games"
              aria-label="Search games"
            />
            {query && results.length > 0 ? (
              <div className="ds-nav-search-results">
                {results.map((name) => (
                  <Link key={name} to={`/${getGameSlug(name)}/accounts`} onClick={() => setQuery('')}>
                    <img src={getGameIcon(name)} alt="" width={24} height={24} />
                    {name}
                  </Link>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="ds-nav-actions">
          {loggedIn ? (
            <UserMenu />
          ) : (
            <>
              <Link to="/auth?signin=true" state={{ backgroundLocation: location }} className="ds-btn ds-btn-ghost">
                Log in
              </Link>
              <Link to="/auth?signin=true" state={{ backgroundLocation: location }} className="ds-btn ds-btn-lime">
                Sign up
              </Link>
            </>
          )}
          <Link to={authTo('/messages')} state={authState} className="ds-chat">
            <span>
              <strong>Join Live Chat</strong>
              <small>
                <img src="/ds/icon-online.svg" alt="" width={12} height={12} />
                {unread || 0}
              </small>
            </span>
            <img src="/ds/icon-chat.svg" alt="" width={24} height={24} />
          </Link>
        </div>
      </header>

      <aside className="ds-side">
          <div className="ds-side-scroll" ref={sideScrollRef}>
            <div className="ds-side-scroll-inner">
            <div className="ds-tabs">
              <Link to="/" className={`ds-tab${sellerMode ? '' : ' is-on'}`}>
                Customer
              </Link>
              <Link
                to={authTo(isSeller ? '/profile/seller/my-items' : '/profile/become-seller')}
                state={isSeller ? undefined : authState}
                className={`ds-tab${sellerMode ? ' is-on' : ''}`}
              >
                {isSeller ? 'Seller' : 'Become a seller'}
              </Link>
            </div>
            {isProfile ? (
              <ProfileDsNav />
            ) : (
              <>
            <div className="ds-side-items">
              {SIDE_LINKS.map((item) => (
                <SideItem
                  key={item.label}
                  ico={item.ico}
                  label={item.label}
                  href={item.href}
                  to={item.auth ? authTo(item.to) : item.to}
                  state={item.auth ? authState : undefined}
                />
              ))}
            </div>
            <div className="ds-acc">
              <button type="button" onClick={() => setCategoriesOpen((open) => !open)}>
                Categories
                <CaretDown size={16} style={{ transform: categoriesOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {categoriesOpen
                ? SIDE_CATEGORIES.map((item) => (
                    <SideItem key={item.to} to={item.to} ico={item.ico} label={item.label} />
                  ))
                : null}
            </div>
              </>
            )}
            <div className="ds-side-bottom">
            <Link to={authTo('/profile/wallet')} state={authState} className="ds-promo">
              <span className="ds-promo-media" aria-hidden>
                <img src="/ds/wallet.png" alt="" width={181} height={120} />
              </span>
              <span className="ds-promo-copy">
                <span className="ds-promo-kicker">Crypto and Fiat</span>
                <span className="ds-promo-title">Fast Deposit and<br />Withdrawal</span>
              </span>
            </Link>
            <div className="ds-side-foot">
              <div className="ds-foot-links">
                <Link to={authTo('/profile/become-seller')} state={authState} className="ds-foot-link">
                  <span className="ds-foot-link-ico" style={{ '--ico': 'url(/ds/side-affiliate.svg)' }} aria-hidden />
                  Affiliate Program
                </Link>
                <Link to={authTo('/messages')} state={authState} className="ds-foot-link">
                  <span className="ds-foot-link-ico" style={{ '--ico': 'url(/ds/side-support.svg)' }} aria-hidden />
                  Live Support
                </Link>
              </div>
              <div className="ds-lang-wrap" ref={langRef}>
                <button
                  type="button"
                  className={`ds-lang${langOpen ? ' is-open' : ''}`}
                  aria-expanded={langOpen}
                  aria-haspopup="listbox"
                  onClick={() => setLangOpen((open) => !open)}
                >
                  <span className="ds-flag">
                    <img src={flagSrc} alt="" width={21} height={21} />
                  </span>
                  <span className="ds-lang-name">{currentLanguageData?.name || 'English'}</span>
                  <img className="ds-lang-chevron" src="/ds/lang-chevron.svg" alt="" width={20} height={20} />
                </button>
                {langOpen ? (
                  <div className="ds-lang-menu" role="listbox">
                    {supportedLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        type="button"
                        role="option"
                        aria-selected={currentLanguage === lang.code}
                        className={`ds-lang-option${currentLanguage === lang.code ? ' is-on' : ''}`}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setLangOpen(false);
                        }}
                      >
                        <span className="ds-flag">
                          <img src={LANG_FLAGS[lang.code] || '/flags/gb.svg'} alt="" width={21} height={21} />
                        </span>
                        {lang.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="ds-socials">
                {SOCIALS.map((item) => (
                  <a
                    key={item.label}
                    className="ds-social"
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={item.label}
                  >
                    <span className="ds-social-ico" style={{ '--ico': `url(${item.ico})` }} aria-hidden />
                  </a>
                ))}
              </div>
            </div>
            </div>
            </div>
          </div>
        </aside>
        {children ? (
          <div className="ds-body">
            <div className="ds-main">{children}</div>
          </div>
        ) : null}

      <MobileTabBar
        hidden={false}
        browseOpen={false}
        onBrowse={() => navigate('/fortnite/accounts')}
      />
    </div>
  );
}
